// services/researchService.js
//
// The research workbench's read side of the market-data lake, through
// crypto-backend-api routes/marketData.js (JWT; the browser never talks to
// the lake). Three tools, one service:
//
//   GET /api/market-data/event-study?type=&tickers=&from=&to=&…   (Event Lab)
//     -> { type, tickers, curve: [{ t, aar, caar, lo, hi }], tests: [{ window:[a,b],
//          label, n, mean, median, sd, se, t, hitRate, ci:[lo,hi], p, q }],
//          events: [{ ticker, session, car: { '0:5': … }, meta, advUsd, model }],
//          n: { events, used, skipped }, skipped: [{ ticker, date, reason }],
//          benchmark, model, bootstrap, datasetVersion }
//   GET /api/market-data/edge                    (Edge Library catalog)
//     -> { studies: [{ id, name, hypothesis, source, method, headlineMetric,
//          expectedSign, params, paramDocs, latest: summary|null }], defaults, verdict }
//   GET /api/market-data/edge/:id[?run=&timeframe=&market=&tier=] -> a stored result (404 until computed)
//   GET /api/market-data/edge/:id/history        -> { id, runs: [summary] }
//   GET /api/market-data/screen?asOf=&where=&names=&…   (point-in-time screen)
//     -> { asOf, session, names, where: [{name, op, value}], sort, universe,
//          n: { screened, matched, returned, noData }, rows: [{ ticker, cik, close, <name>… }],
//          datasetVersion, elapsedMs }
//   GET /api/market-data/signals/catalog         -> { bare: [{name, family, group, description}],
//          windowed: [{pattern, family, group, maxWindow, unit, description}],
//          price: [{pattern, description, maxWindow, group, unit, crossSectional, adjusted, engine}] }
//   GET /api/market-data/factor-lab?signal=&from=&to=&rebalance=&…   (Factor Lab)
//     -> study-shaped: { id, name, signal, names, params, period, universe, n: { rebalances,
//          analysed, names, avgNames, signalCoverage }, headline, full: { value, ci, p, hitRate,
//          spread: { sharpe, cumulative:[{date,value}] }, buckets:[{bucket, mean, …}] }, recent,
//          ic: { value, ci, p, t, ir, n, horizon }, icDecay: [{ horizon, mean, t, ci, p, n, ir, hitRate }],
//          turnover: { top, bottom, autocorr, n }, sectorNeutral: { …summary, coverage, note },
//          capacity, benchmarkFound, notes, edgeReport, datasetVersion, elapsedMs }
//   GET /api/market-data/factor-lab/catalog      -> { price: [{pattern, description}], signals: {bare, windowed},
//          functions: [name], examples: [{ name, signal, note }], rebalance: [..], defaults, limits }
//   GET /api/market-data/sectors?tickers=        -> { rows: [{ ticker, cik, sic, sector, industry }], coverage, datasetVersion }
//   GET /api/market-data/signal-blend?signals=A|B|C&labels=&weighting=&standardize=&…   (Signal Blend)
//     -> the Factor Lab shape for the blended score, plus signals: [{ label, expression, names,
//          coverage, n, ic, spread, turnover, icDecay, avgWeight, weightNow }],
//          baselines: { equal, bestSingle }, diversification: { blendIc, equalIc, bestSingle,
//          bestSingleIc, meanSingleIc, icVsBest, icVsMean, blendIr, bestSingleIr },
//          correlation: { labels, matrix, avg, max, n }, weights: { labels, avg, rows: [{ date, source, weights }] },
//          n: { …, warmup, weightSources }
//   GET /api/market-data/signal-blend/catalog    -> { separator, weighting: [{value, description}],
//          standardize: [..], defaults, limits, examples: [{ signals: [..], labels: [..] }] }
//   GET /api/market-data/ideas[?asOf=]           (the daily candidate feed, plan B-E2)
//     -> { asOf, session, builtAt, builtFor, stale, staleNote, reason, requestedAsOf?, windowNote?,
//          universe, ideas: [{ studyId, name, hypothesis, grade, confidence, verdict, headline,
//          chart, candidates: [{ ticker, why, cost, capacity, since, values }], unbound?,
//          unboundReason?, binding?, skipped?, skippedNote?, datasetVersion }],
//          library, notes, datasetVersion }
//   GET /api/market-data/model[?asOf=]           (the one curated multi-factor model, plan B-E3)
//     -> { asOf, builtAt, builtFor, stale, staleNote, reason, model: { recipe, signals, labels,
//          rebalance, hold, weighting, weights: { date, source, labels, values }|null },
//          grade, confidence, verdict, edgeReport, headline, names: [{ ticker, rank, score,
//          sector, parts: { <label>: z } }], notes, datasetVersion, error? }
//
// Both are PERSISTED artefacts on the lake, built once a night by its `ideas`
// leg — a page view never runs a screen. That is why the Research home reads
// each exactly once per mount and never on a keystroke.
//
// ─── Contract notes ─────────────────────────────────────────────────────────
//
// 1. Every call here IS a 503 when the lake is unset (`MARKET_DATA_DISABLED`)
//    or down (`MARKET_DATA_DOWN`), 504 (`MARKET_DATA_TIMEOUT`) when a slow
//    call outran the api's budget, 400 with the lake's own message for a bad
//    parameter (an unknown signal name, an unparsable `where`, a ticker list
//    over the cap), and the lake's 404 for a study that has never been
//    computed. `lakeState(err)` turns those into the sentence a page shows.
//
// 2. Event study and screen are slow by nature (seconds to a minute on a wide
//    universe). The pages keep the form usable and show elapsed time; nothing
//    here retries.
//
// 3. The Edge Library is read-only from the browser on purpose: runs happen
//    on the lake box on a schedule. A study with `latest: null` has not been
//    computed yet — that is a state to render, not an error.
import http from './http';

const clean = (params) => {
  const out = {};
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    out[k] = Array.isArray(v) ? v.join(',') : String(v);
  });
  return out;
};

const qs = (params) => {
  const p = new URLSearchParams(clean(params)).toString();
  return p ? `?${p}` : '';
};

export const getEventStudy = (params) =>
  http.get(`/api/market-data/event-study${qs(params)}`).then((res) => res?.data || null);

export const getEdgeCatalog = () =>
  http.get('/api/market-data/edge').then((res) => {
    const d = res?.data || {};
    return {
      studies: Array.isArray(d.studies) ? d.studies : [],
      defaults: d.defaults || {},
      verdict: d.verdict || {},
      // `library` says how many studies the BH correction spanned and when
      // (lake `edge/library.js libraryInfo`: { studies, corrected, correctedAt }).
      // The board's footer reads it; an older lake simply has none.
      library: d.library || null,
      // How old the board is (lake PLAN-NIGHTLY unit 5): the last night that
      // measured >= 90 % of the library, the newest night's coverage, partial /
      // pending. Null on an older lake — the Research pages then show no banner.
      staleness: d.staleness && typeof d.staleness === 'object' ? d.staleness : null,
    };
  });

// The nightly idea changelog (lake PLAN-NIGHTLY unit 8, GET /edge/changelog
// through the api): { days, nights: [{ date, added: [{ id, kind, family,
// source, hypothesis }], transitions, retired: [{ id, from, to, reason }],
// promoted: [...], skippedIdeas, llm }] }, newest night first.
export const getEdgeChangelog = (days = 7) =>
  http.get(`/api/market-data/edge/changelog${qs({ days })}`).then((res) => {
    const d = res?.data || {};
    return { days: d.days || days, nights: Array.isArray(d.nights) ? d.nights : [] };
  });

// Queue an idea for tonight's run (PUT /edge/drafts through the api, trade
// scope): { kind: 'ranked', spec: { expr, … } } or { kind: 'event', spec:
// { where, … } }. The lake compiles it and answers 400 naming what is wrong
// (lakeState(err).message), or { queued, id } / { queued: false, duplicate }.
export const queueEdgeDraft = ({ kind, spec, family }) =>
  http.put('/api/market-data/edge/drafts', { kind, spec, ...(family ? { family } : {}) }).then((res) => res?.data || null);

// `timeframe` (PLAN-EDGE-EVIDENCE.md 4.4): an intraday study is its own cell
// under the SAME id, so the bar travels as `?timeframe=1h`. Daily sends none,
// and the URL is exactly the one it always was.
//
// `address` (PLAN-CRYPTO-EDGE C8): a crypto cell is `{ market: 'crypto', tier }`
// on top of the bar — `?market=crypto&timeframe=4h&tier=mid` (no tier = the
// pooled cell). A stock study sends neither, so its URL is unchanged.
const addressQuery = (address) => (address && address.market === 'crypto'
  ? { market: 'crypto', tier: address.tier || undefined }
  : {});

export const getEdgeStudy = (id, run, timeframe, address) =>
  http.get(`/api/market-data/edge/${encodeURIComponent(id)}${qs({ run, ...addressQuery(address), timeframe })}`).then((res) => res?.data || null);

export const getEdgeHistory = (id, limit, timeframe, address) =>
  http.get(`/api/market-data/edge/${encodeURIComponent(id)}/history${qs({ limit, ...addressQuery(address), timeframe })}`).then((res) => {
    const d = res?.data || {};
    return { id: d.id || id, runs: Array.isArray(d.runs) ? d.runs : [] };
  });

export const getScreen = (params) =>
  http.get(`/api/market-data/screen${qs(params)}`).then((res) => res?.data || null);

export const getSignalsCatalog = () =>
  http.get('/api/market-data/signals/catalog').then((res) => {
    const d = res?.data || {};
    // `price` (lake P3.2): the price and indicator terms a screen filter or an
    // Event Lab rule may use, each { pattern, description, group, unit,
    // crossSectional, adjusted, engine, maxWindow }. An older lake has none.
    return { bare: Array.isArray(d.bare) ? d.bare : [], windowed: Array.isArray(d.windowed) ? d.windowed : [], price: Array.isArray(d.price) ? d.price : [] };
  });

export const getFactorLab = (params) =>
  http.get(`/api/market-data/factor-lab${qs(params)}`).then((res) => res?.data || null);

export const getFactorLabCatalog = () =>
  http.get('/api/market-data/factor-lab/catalog').then((res) => {
    const d = res?.data || {};
    return {
      price: Array.isArray(d.price) ? d.price : [],
      signals: { bare: Array.isArray(d.signals?.bare) ? d.signals.bare : [], windowed: Array.isArray(d.signals?.windowed) ? d.signals.windowed : [] },
      functions: Array.isArray(d.functions) ? d.functions : [],
      examples: Array.isArray(d.examples) ? d.examples : [],
      rebalance: Array.isArray(d.rebalance) ? d.rebalance : ['daily', 'weekly', 'monthly'],
      defaults: d.defaults || {},
      limits: d.limits || {},
    };
  });

export const getSectors = (tickers, asOf) =>
  http.get(`/api/market-data/sectors${qs({ tickers, asOf })}`).then((res) => res?.data || null);

/** `signals` / `labels` may be arrays — they are joined with the lake's `|` separator (commas live inside `max(A, B)`). */
export const getSignalBlend = (params) => {
  const p = { ...params };
  if (Array.isArray(p.signals)) p.signals = p.signals.join(' | ');
  if (Array.isArray(p.labels)) p.labels = p.labels.join(' | ');
  return http.get(`/api/market-data/signal-blend${qs(p)}`).then((res) => res?.data || null);
};

export const getSignalBlendCatalog = () =>
  http.get('/api/market-data/signal-blend/catalog').then((res) => {
    const d = res?.data || {};
    return {
      separator: d.separator || '|',
      weighting: Array.isArray(d.weighting) ? d.weighting : [],
      standardize: Array.isArray(d.standardize) ? d.standardize : ['zscore', 'rank'],
      defaults: d.defaults || {},
      limits: d.limits || {},
      examples: Array.isArray(d.examples) ? d.examples : [],
    };
  });

// ─── The edge engine's two nightly feeds (plan B-E2 / B-E3) ─────────────────
//
// `asOf` is the ONLY key either route takes (the api's whitelist drops
// everything else), and asking for a past date does not recompute anything —
// the lake answers the digest it has and says so in `windowNote`. The raw
// payload passes through untouched: the three signals `reason` / `stale` /
// `builtFor` are rendered differently by the page and must not be collapsed
// into one "empty" here.

/** The daily candidate feed: each currently-valid study joined with today's cross-section. */
export const getIdeas = (params) =>
  http.get(`/api/market-data/ideas${qs(params)}`).then((res) => res?.data || null);

/** The one curated multi-factor model (quality + value + momentum + insider) and its grade. */
export const getModel = (params) =>
  http.get(`/api/market-data/model${qs(params)}`).then((res) => res?.data || null);

/** Mirrors the api's ticker-list check (1–100 symbols for the event study) so the UI refuses before a round trip. */
export const parseTickers = (text, max = 100) => {
  const list = String(text || '').toUpperCase().split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
  const unique = [...new Set(list)];
  if (unique.length === 0) return { tickers: [], error: 'Enter at least one ticker.' };
  if (unique.length > max) return { tickers: unique, error: `At most ${max} tickers.` };
  const bad = unique.find((t) => !/^[A-Z0-9.-]{1,12}$/.test(t));
  if (bad) return { tickers: unique, error: `"${bad}" is not a ticker (1–12 characters: A–Z, 0–9, dot, dash).` };
  return { tickers: unique, error: '' };
};

// ─── "Explain this" (PLAN-RESEARCH-UX.md, B-E6) ─────────────────────────────
//
//   POST /api/research/explain { kind, ref: { id, runId? }, payload }
//     -> { explanation, cached, model, tokens }
//
// This one is the api's own route (crypto-backend-api routes/research.js), not
// a lake proxy, so `lakeState` does not describe it: 503 `AI_DISABLED` means
// the server has no model key, 429 is the per-user hourly limit, 413 means the
// payload was too big. `explainState(err)` is the sentence for each.
//
// The payload is the *result object the page is already showing* — an Edge
// Report plus the headline figures — and never a curve, a trade list or a
// series: the api reads a fixed set of fields, and a page builds its payload
// by copying those fields out, so a series cannot ride along by accident.

/** The result objects the api's `services/researchExplain.js` accepts. */
export const EXPLAIN_KINDS = ['edge_study', 'backtest', 'factor_lab', 'blend', 'event_study'];
/** The api refuses over 64 KB; we refuse first, so a reader gets a sentence rather than a 413. */
export const MAX_EXPLAIN_BYTES = 60 * 1024;

/** Bytes the payload would weigh on the wire (the api measures the same JSON). */
export const explainBytes = (payload) => {
  let json;
  try { json = JSON.stringify(payload === undefined ? null : payload); } catch { return Infinity; }
  if (typeof json !== 'string') return Infinity;
  if (typeof TextEncoder === 'function') return new TextEncoder().encode(json).length;
  return json.length;
};

export const explain = ({ kind, ref, payload }) => {
  if (!EXPLAIN_KINDS.includes(kind)) return Promise.reject(new Error(`kind must be one of ${EXPLAIN_KINDS.join(', ')}`));
  if (explainBytes(payload) > MAX_EXPLAIN_BYTES) {
    return Promise.reject(Object.assign(new Error('This result is too large to explain.'), { code: 'PAYLOAD_TOO_LARGE' }));
  }
  const body = { kind, payload };
  if (ref && (ref.id || ref.runId)) {
    body.ref = {};
    if (ref.id) body.ref.id = String(ref.id).slice(0, 128);
    if (ref.runId) body.ref.runId = String(ref.runId).slice(0, 128);
  }
  return http.post('/api/research/explain', body).then((res) => res?.data || null);
};

/** One sentence for a failed explain call, in the reader's words. */
export const explainState = (err) => {
  const status = err?.response?.status;
  const code = err?.response?.data?.code || err?.code;
  if (code === 'AI_DISABLED' || status === 503) return { kind: 'off', message: 'Explanations are off on this server' };
  // A 404 is not a broken request: the route itself is not on this api yet.
  // Production runs a build without it until the next deploy, so the FIRST
  // thing a user there would meet is a 404 — it has to read as a capability
  // this server lacks, not as a failure they caused.
  if (status === 404) return { kind: 'missing', message: "Explain isn't available on this server yet" };
  if (status === 429) return { kind: 'limited', message: 'Try again in a while' };
  if (code === 'PAYLOAD_TOO_LARGE' || status === 413) return { kind: 'big', message: 'This result is too large to explain.' };
  if (code === 'AI_PROVIDER' || status === 502) return { kind: 'provider', message: 'The explainer did not answer; try again in a minute.' };
  if (status === 401 || status === 403) return { kind: 'auth', message: 'Your session has expired; sign in again.' };
  return {
    kind: 'error',
    message: err?.response?.data?.error || err?.message || 'The explanation failed; try again in a minute.',
  };
};

/** The states where asking again cannot help: the server does not have the feature. */
export const EXPLAIN_UNAVAILABLE = ['off', 'missing'];

/**
 * One sentence for a failed lake call, and whether it is the lake's state
 * (not configured / down / timed out) rather than the request's fault.
 */
export const lakeState = (err) => {
  const status = err?.response?.status;
  const code = err?.response?.data?.code;
  const message = err?.response?.data?.error || err?.message || 'Request failed.';
  if (code === 'MARKET_DATA_DISABLED') return { kind: 'disabled', message: 'The API is not connected to a market-data lake, so the research tools are unavailable.' };
  if (code === 'MARKET_DATA_DOWN' || status === 503) return { kind: 'down', message: 'The market-data lake is unreachable right now; try again in a minute.' };
  if (code === 'MARKET_DATA_TIMEOUT' || status === 504) return { kind: 'timeout', message: 'The lake did not answer in time — narrow the request (fewer tickers, a smaller universe).' };
  if (status === 404) return { kind: 'notfound', message };
  if (status === 400) return { kind: 'bad', message };
  if (status === 401 || status === 403) return { kind: 'auth', message: 'Your session has expired; sign in again.' };
  return { kind: 'error', message };
};

const researchService = { getEventStudy, getEdgeCatalog, getEdgeChangelog, queueEdgeDraft, getEdgeStudy, getEdgeHistory, getScreen, getSignalsCatalog, getFactorLab, getFactorLabCatalog, getSectors, getSignalBlend, getSignalBlendCatalog, getIdeas, getModel, parseTickers, lakeState, explain, explainState, explainBytes, EXPLAIN_UNAVAILABLE };

export default researchService;
